import { FC, ReactNode, useEffect, useRef } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  setCurrentTenant,
  setMe,
  setOperationCountry,
  setTenantEnabledFeatures,
  useCurrentSubdomain,
  useCurrentTenant,
  useCurrentUser,
} from '_graphql/cache/auth';
import {
  GET_ORGANIZATION_BY_SLUG_MIN,
  GET_ORGANIZATION_ENABLED_FEATURES,
  ORGANIZATION_OPERATION_COUNTRIES,
} from '_graphql/operations/organization';

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * TenantProvider
 *
 * Syncs better-auth session data with apollo cache state.
 * Responsibilities:
 * 1. Sync user data from session to apollo cache
 * 2. Sync active organization from better-auth to apollo cache
 * 3. Fetch and cache user permissions
 * 4. Fetch and cache organization features
 * 5. Keep state up-to-date when organization switches in apollo cache
 */
export const TenantProvider: FC<TenantProviderProps> = ({ children }) => {
  const currentTenant = useCurrentTenant();
  const currentUser = useCurrentUser();
  const currentSubdomain = useCurrentSubdomain();

  // Track what we've already fetched to prevent duplicate requests
  const fetchedOrgSlugRef = useRef<string | null>(null);
  const fetchedFeaturesTenantIdRef = useRef<string | null>(null);

  const [getOrgBySlug, { data: getOrgBySlugData }] = useLazyQuery(
    GET_ORGANIZATION_BY_SLUG_MIN,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const [getEnabledFeatures, { data: getEnabledFeaturesData }] = useLazyQuery(
    GET_ORGANIZATION_ENABLED_FEATURES,
    {
      fetchPolicy: 'network-only',
    }
  );

  // Track previous tenant ID to detect changes for operation countries
  const prevTenantIdRef = useRef<string | null>(null);

  const {
    data: operationCountriesData,
    loading,
    refetch: refetchOperationCountries,
  } = useQuery(ORGANIZATION_OPERATION_COUNTRIES, {
    variables: { organizationId: currentTenant?.id },
    fetchPolicy: 'network-only',
    skip: !currentTenant?.id,
  });

  // Sync user data from session to apollo cache
  useEffect(() => {
    if (currentUser) {
      setMe(currentUser);
    }
  }, [currentUser]);

  // Sync organizations from GraphQL to apollo cache
  useEffect(() => {
    if (currentTenant) {
      setCurrentTenant(currentTenant);
    }
  }, [currentTenant]);

  // Fetch org data when subdomain changes and doesn't match current tenant
  useEffect(() => {
    if (!currentSubdomain) return;
    if (currentTenant?.slug === currentSubdomain) return;
    if (fetchedOrgSlugRef.current === currentSubdomain) return;

    fetchedOrgSlugRef.current = currentSubdomain;
    getOrgBySlug({
      variables: { slug: currentSubdomain },
    });
  }, [currentSubdomain, currentTenant?.slug, getOrgBySlug]);

  // Sync fetched org data to cache (separate effect to handle async response)
  useEffect(() => {
    if (getOrgBySlugData?.organizationBySlugMinimal) {
      setCurrentTenant(getOrgBySlugData.organizationBySlugMinimal);
    }
  }, [getOrgBySlugData]);

  // Fetch feature flags when tenant changes
  useEffect(() => {
    if (!currentTenant?.id) return;
    if (fetchedFeaturesTenantIdRef.current === currentTenant.id) return;

    fetchedFeaturesTenantIdRef.current = currentTenant.id;
    getEnabledFeatures({
      variables: { tenantId: currentTenant.id },
    });
  }, [currentTenant?.id, getEnabledFeatures]);

  // Sync fetched features to cache (separate effect to handle async response)
  useEffect(() => {
    if (getEnabledFeaturesData?.tenantEnabledFeatures) {
      setTenantEnabledFeatures(getEnabledFeaturesData.tenantEnabledFeatures);
    }
  }, [getEnabledFeaturesData]);

  // Refetch operation countries when tenant changes
  useEffect(() => {
    if (!currentTenant?.id) return;

    // Only refetch if tenant actually changed
    if (prevTenantIdRef.current !== currentTenant.id) {
      prevTenantIdRef.current = currentTenant.id;
      refetchOperationCountries();
    }
  }, [currentTenant?.id, refetchOperationCountries]);

  // Sync operation countries to cache when data arrives
  useEffect(() => {
    if (operationCountriesData?.organizationOperationCountries) {
      setOperationCountry(
        operationCountriesData.organizationOperationCountries
      );
    }
  }, [operationCountriesData]);

  return <>{children}</>;
};

export default TenantProvider;
